/// <amd-module name="Test/PassRateChartView/Sources/PassRateChartSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { DataSourceHelper } from "Test/Analytics/Common/Utils/DataSourceHelper";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";

export class PassRateChartSource extends TestReportSource {
    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_PassRateChartReport", pageContext);
    }

    public queryTestOutcomeCountMetricData(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        switch (DataSourceHelper.getPassRateCardDataSourceTable(confValues)) {
            case CommonTypes.DataSourceTable.TestResult:
                return this._queryTestOutcomeCountMetricData_TestResultTable(testReportContext, confValues);
            case CommonTypes.DataSourceTable.TestResultDaily:
                return this._queryTestOutcomeCountMetricData_TestResultDailyTable(testReportContext, confValues);
            case CommonTypes.DataSourceTable.TestRun:
                return this._queryTestOutcomeCountMetricData_TestRunTable(testReportContext, confValues);
            default:
                return this._queryTestOutcomeCountMetricData_TestResultTable(testReportContext, confValues);
        }
    }

    private _queryTestOutcomeCountMetricData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            const requiredOutcomeFilters: CommonTypes.TestOutcome[] = [
                CommonTypes.TestOutcome.Failed,
                CommonTypes.TestOutcome.Passed,
                CommonTypes.TestOutcome.NotExecuted,
                CommonTypes.TestOutcome.NotImpacted
            ];
            const outcomeCountStrs = this._getOutcomeCountAggregateAndComputeStrings(requiredOutcomeFilters);

            const filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            const aggrStr = `aggregate(ResultCount with sum as ResultCount, ${outcomeCountStrs.outcomeAggregationStr})`;

            queryOptions.$apply = `${filterStr}/${aggrStr}`;

            return this._queryTestOutcomeCountMetricData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountMetricData_TestResultTable(
        testResultContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testResultContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            const aggrStr = `aggregate($count as TotalCount, ${this._getOutcomeCountCastedAggregationString(
                CommonTypes.TestOutcome.Passed
            )}, ${this._getOutcomeCountCastedAggregationString(CommonTypes.TestOutcome.Failed)}, 
${this._getOutcomeCountCastedAggregationString(CommonTypes.TestOutcome.NotExecuted)}, ${this._getOutcomeCountCastedAggregationString(
                CommonTypes.TestOutcome.NotImpacted
            )})`;

            queryOptions.$apply = `${filterStr}/${aggrStr}`;

            return this._queryTestOutcomeCountMetricData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountMetricData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            const aggrStr = `aggregate(${this._getTotalCountStringForAggregation()}, ${this._getOutcomeCountStringForAggregation(
                CommonTypes.TestOutcome.Passed
            )},
${this._getOutcomeCountStringForAggregation(CommonTypes.TestOutcome.Failed)}, ${this._getOutcomeCountStringForAggregation(
                CommonTypes.TestOutcome.NotExecuted
            )}, ${this._getOutcomeCountStringForAggregation(CommonTypes.TestOutcome.NotImpacted)})`;

            queryOptions.$apply = `${filterStr}/${aggrStr}`;

            return this._queryTestOutcomeCountMetricData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountMetricData(
        queryOptions: ODataQueryOptions,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        const entityWiseOutcomeParseFunction: Function = this._getEntityWiseOutcomeCountParseFunction(queryOptions.entityType);
        const entityWiseTotalCountParseFunction: Function = this._getEntityWiseTotalCountParseFunction(queryOptions.entityType);

        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("Data is empty");
            }
            const metricsData: CommonTypes.ICardMetricsData = {
                passedCount: 0,
                failedCount: 0,
                notExecutedCount: 0,
                notImpactedCount: 0,
                passPercentage: 0,
                totalCount: 0,
                confValues: confValues,
                isCachedData: data.isCachedData
            } as CommonTypes.ICardMetricsData;

            // Iterate over all data. There should be only item in list as we are fetching aggregates and not group.
            (data.value as any[]).forEach(d => {
                metricsData.totalCount = entityWiseTotalCountParseFunction(d);
                metricsData.notImpactedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotImpacted, d);
                metricsData.passedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Passed, d);
                metricsData.failedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Failed, d);
                metricsData.notExecutedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotExecuted, d);

                metricsData.passPercentage = this._getPassPercentage(
                    metricsData.totalCount,
                    metricsData.passedCount,
                    metricsData.notExecutedCount,
                    metricsData.notImpactedCount
                );
            });
            return metricsData;
        });
    }
}
